import { jsx } from '@amazon/vinyl-tsx'
import { data } from '@amazon/vinyl-observable'
import type { ObservableValue } from '@amazon/vinyl-observable'
import { Modal } from '@/components/Modal'
import { openModal } from '@/components/modalLayer'
import {
    getStringInactivityModal,
    getStringCookieConsentModal,
    getStringDailyChangeLimitModal,
} from '@/messaging/i18n/bundle.generated'

export interface SystemModalOptions {
    readonly title: ObservableValue<string>
    readonly body?: ObservableValue<string>
    readonly primary?: { label: ObservableValue<string>; run: () => void }
    readonly secondary?: { label: ObservableValue<string>; run: () => void }
    readonly className?: string
    readonly dismissible?: boolean
}

/**
 * Shared layout for an informational / friction-style modal.
 * Mirrors GH's Default* modal shape — title + body + 1-2 actions —
 * routed through the app modal layer so any surface can pop one
 * without owning a portal.
 */
export function openSystemModal(options: SystemModalOptions): void {
    openModal({
        render: (close) => {
            const visible = data(true)
            const closeWithFade = () => {
                visible.value = false
                queueMicrotask(close)
            }

            const primaryRun = options.primary?.run ?? closeWithFade
            const dismissible = options.dismissible !== false

            return (
                <Modal
                    className={`systemModal ${options.className ?? ''}`}
                    visible={visible}
                    onClose={dismissible ? closeWithFade : () => undefined}
                >
                    <h2>{options.title}</h2>
                    {options.body ? <p>{options.body}</p> : null}
                    <div className="systemModalActions">
                        {options.secondary ? (
                            <button
                                className="textButton secondary"
                                onclick={() => {
                                    options.secondary?.run()
                                    closeWithFade()
                                }}
                            >
                                {options.secondary.label}
                            </button>
                        ) : null}
                        <button
                            className="button primary"
                            onclick={() => {
                                primaryRun()
                                closeWithFade()
                            }}
                        >
                            {options.primary?.label ?? data('OK')}
                        </button>
                    </div>
                </Modal>
            )
        },
    })
}

/**
 * Long-idle session timeout warning. Pop this when the player has
 * been playing for a long stretch without user interaction (ghost
 * listener detection). Mirrors GH's InactivityModal — three
 * affordances: close X (via dismissible), Continue Listening
 * (primary), Dismiss (secondary).
 */
export function openInactivityModal(options: {
    onContinue: () => void
    onDismiss?: () => void
}): void {
    openSystemModal({
        className: 'inactivityModal',
        title: getStringInactivityModal('dmusic_amp_inactive_playback_text'),
        primary: {
            label: getStringInactivityModal('dmusic_amp_inactive_playback_button'),
            run: options.onContinue,
        },
        secondary: {
            label: getStringInactivityModal('dmusic_amp_inactive_playback_dismiss'),
            run: () => options.onDismiss?.(),
        },
        dismissible: true,
    })
}

/**
 * Cookie / tracking consent prompt. Pop this on first launch when
 * `userPreferences.consentInfo` indicates the user hasn't responded
 * to the cookie banner. Mirrors GH's CookieConsentModal — accept
 * runs the consent mutation; deny dismisses without preference.
 */
export function openCookieConsentModal(options: {
    onAccept: () => void
    onDecline: () => void
}): void {
    openSystemModal({
        className: 'cookieConsentModal',
        title: getStringCookieConsentModal('cookieConsentLegaleseHeaderText'),
        body: getStringCookieConsentModal('cookieConsentLegaleseContent', {
            cookieNoticeUrl: '',
            vendorCount: '',
            cookiePreferenceUrl: '',
            privacyNoticeUrl: '',
        }),
        primary: {
            label: getStringCookieConsentModal('cookieConsentAcceptButtonText'),
            run: options.onAccept,
        },
        secondary: {
            label: getStringCookieConsentModal('cookieConsentDenyButtonText'),
            run: options.onDecline,
        },
        dismissible: false,
    })
}

/**
 * Daily-change-limit modal. Surface when the gateway returns the
 * over-limit error on a free-tier playlist edit. Mirrors GH's
 * DailyChangeLimitModal — read-only acknowledgement.
 */
export function openDailyChangeLimitModal(): void {
    openSystemModal({
        className: 'dailyChangeLimitModal',
        title: getStringDailyChangeLimitModal('dmusic_daily_change_limit_modal_title'),
        body: getStringDailyChangeLimitModal('dmusic_daily_change_limit_modal_body'),
        primary: {
            label: getStringDailyChangeLimitModal('dmusic_daily_change_limit_modal_button'),
            run: () => undefined,
        },
    })
}
